import api from "./api";

/**
 * Lightweight offline queue for public reflection / feedback submissions.
 * Stores pending submissions in localStorage and flushes them when online.
 *
 * Each item: { id, endpoint, payload, meta, queued_at, attempts }
 */

const KEY = "ccs_offline_queue";

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
};

const write = (q) => {
  localStorage.setItem(KEY, JSON.stringify(q));
  // Notify any listeners in this tab (storage event only fires across tabs).
  window.dispatchEvent(new CustomEvent("ccs-queue-changed", { detail: { count: q.length } }));
};

export const queueSubmission = (endpoint, payload, meta = {}) => {
  const q = read();
  q.push({
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    endpoint,
    payload,
    meta,
    queued_at: new Date().toISOString(),
    attempts: 0,
  });
  write(q);
  return q.length;
};

export const getQueue = () => read();
export const getQueueCount = () => read().length;

export const clearQueue = () => write([]);

export const flushQueue = async (onProgress) => {
  let q = read();
  if (q.length === 0) return { synced: 0, failed: 0, total: 0 };
  const remaining = [];
  let synced = 0;
  let failed = 0;
  for (const item of q) {
    try {
      await api.post(item.endpoint, item.payload);
      synced++;
    } catch (err) {
      const status = err?.response?.status;
      // 4xx (validation, not-found) → drop; 5xx/network → keep and retry later.
      if (status && status >= 400 && status < 500) {
        failed++;
      } else {
        remaining.push({ ...item, attempts: (item.attempts || 0) + 1 });
        failed++;
      }
    }
    if (onProgress) onProgress({ synced, failed, total: q.length });
  }
  write(remaining);
  return { synced, failed, total: q.length };
};

export const isOnline = () =>
  typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true;

/**
 * React hook helper: subscribe to queue + online/offline changes.
 * Returns { online, queueCount }.
 */
export const subscribeToQueue = (callback) => {
  const handler = () => callback({ online: isOnline(), queueCount: getQueueCount() });
  window.addEventListener("online", handler);
  window.addEventListener("offline", handler);
  window.addEventListener("ccs-queue-changed", handler);
  window.addEventListener("storage", (e) => { if (e.key === KEY) handler(); });
  return () => {
    window.removeEventListener("online", handler);
    window.removeEventListener("offline", handler);
    window.removeEventListener("ccs-queue-changed", handler);
  };
};
